import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { INDIAN_STATES } from '../../data/indianStates';
import AccountSessionError from './AccountSessionError';
import {
  AUTH_STORAGE_KEY,
  apiGetProfile,
  apiCreateProfile,
  buildCreateProfileBody,
  clearStoredAuth,
  clearStoredAuthIfUnauthorized,
  getFirstValue,
  notifyAuthUpdated,
  parseCreateProfileValidationErrors,
  profileFetchErrorMessage,
  profilePayloadToForm,
  profileSaveErrorMessage,
  readStoredAuth,
  SIGN_IN_REQUIRED_MESSAGE,
  validateAccountHeader,
  validateProfileFormFields,
  ageFromIsoDateInput,
} from './profileFormUtils';
import { stripPhoneDigits, sanitizePhoneDigitsInput, formatPhoneForDisplay } from '../../utils/phone';
import AccountIdentityChangeModal from './AccountIdentityChangeModal';
import './AccountLayout.css';

/** DOM ids for focus + aria-describedby (must match input `id`s) */
const ACCOUNT_FIELD_IDS = {
  username: 'acct-username',
  email: 'acct-email',
  phone_number: 'acct-phone',
  first_name: 'acct-fn',
  last_name: 'acct-ln',
  gender: 'acct-gender',
  date_of_birth: 'acct-dob',
  country: 'acct-country',
  state: 'acct-state',
  city: 'acct-city',
  pincode: 'acct-pin',
  guardian_name: 'acct-gn',
  guardian_email: 'acct-ge',
  guardian_mobile: 'acct-gm',
};

function ProfileReadonlyRow({ label, value, onChange }) {
  const display = value != null && String(value).trim() !== '' ? String(value).trim() : '—';
  return (
    <div className="account-profile-view__row">
      <dt>{label}</dt>
      <dd>
        {display}
        {onChange ? (
          <button type="button" className="account-profile-view__change-btn" onClick={onChange}>
            Change
          </button>
        ) : null}
      </dd>
    </div>
  );
}

function InlineFieldError({ fieldKey, fieldErrors }) {
  const msg = fieldErrors[fieldKey];
  if (!msg) return null;
  const fid = ACCOUNT_FIELD_IDS[fieldKey];
  if (!fid) return null;
  return (
    <p id={`${fid}-err`} className="account-page__field-error" role="alert">
      {msg}
    </p>
  );
}

function CompactField({ id, label, error, children, hint, hintId }) {
  return (
    <div className="account-profile-view__field">
      <label htmlFor={id}>{label}</label>
      <div className="account-profile-view__field-control">
        {children}
        {hint ? (
          <p id={hintId} className="account-profile-view__field-hint">
            {hint}
          </p>
        ) : null}
        {error}
      </div>
    </div>
  );
}

/**
 * Scroll the whole row (label + control + error) into view from the top, then focus without
 * the browser jumping to the caret at the end of long values.
 */
function scrollAccountFormGroupIntoViewAndFocus(fieldEl) {
  if (!fieldEl || !(fieldEl instanceof HTMLElement)) return;
  const group =
    fieldEl.closest('.account-profile-view__field') || fieldEl.closest('.auth-form__group');
  const scrollTarget = group || fieldEl;
  scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });

  const applyFocus = () => {
    try {
      fieldEl.focus({ preventScroll: true });
    } catch {
      try {
        fieldEl.focus();
      } catch {
        /* ignore */
      }
    }
    if (fieldEl instanceof HTMLInputElement) {
      const t = fieldEl.type;
      if (
        t === 'text' ||
        t === 'email' ||
        t === 'tel' ||
        t === 'search' ||
        t === 'url' ||
        t === 'password' ||
        t === ''
      ) {
        try {
          fieldEl.setSelectionRange(0, 0);
        } catch {
          /* date/time/etc. */
        }
      }
    }
  };

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(applyFocus);
  });
}

const emptyForm = {
  first_name: '',
  last_name: '',
  gender: '',
  country: 'India',
  state: '',
  city: '',
  pincode: '',
  date_of_birth: '',
  guardian_name: '',
  guardian_email: '',
  guardian_mobile: '',
};

const AccountUpdateDetails = () => {
  const navigate = useNavigate();
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorFocusKey, setErrorFocusKey] = useState(null);

  const [account, setAccount] = useState({
    id: '',
    username: '',
    email: '',
    phone_number: '',
  });
  const [form, setForm] = useState(() => ({ ...emptyForm }));
  const [identityChangeType, setIdentityChangeType] = useState(null);
  const saveFeedbackRef = useRef(null);

  const clearFieldError = useCallback((name) => {
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setErrorFocusKey((prev) => (prev === name ? null : prev));
  }, []);

  const mergeStoredIdentity = useCallback((normalized) => {
    const stored = readStoredAuth();
    const u = stored.user || {};
    const next = { ...normalized };
    if ((next.id === undefined || next.id === null || next.id === '') && u.id != null) {
      next.id = u.id;
    }
    if (!next.email && u.email) next.email = u.email;
    if (!next.username && u.username) next.username = String(u.username).trim();
    if (!next.phone_number && u.mobile) {
      next.phone_number = stripPhoneDigits(u.mobile);
    }
    return next;
  }, []);

  const loadProfile = useCallback(async () => {
    setLoadError('');
    setFieldErrors({});
    setErrorFocusKey(null);
    setFetching(true);
    const stored = readStoredAuth();
    const token = stored.token;
    if (!token) {
      clearStoredAuth();
      setLoadError(SIGN_IN_REQUIRED_MESSAGE);
      setFetching(false);
      return;
    }
    try {
      const { res, data } = await apiGetProfile(token);
      if (!res.ok) {
        clearStoredAuthIfUnauthorized(res.status);
        throw new Error(profileFetchErrorMessage(res.status, data));
      }
      let normalized = profilePayloadToForm(data);
      normalized = mergeStoredIdentity(normalized);

      if (normalized.id === undefined || normalized.id === null || normalized.id === '') {
        throw new Error('Profile response did not include user id.');
      }

      setAccount({
        id: normalized.id,
        username: normalized.username || '',
        email: normalized.email || '',
        phone_number: normalized.phone_number || '',
      });
      setForm({ ...emptyForm, ...normalized.form });
    } catch (e) {
      setLoadError(e.message || 'Failed to load profile.');
      const fallback = mergeStoredIdentity(profilePayloadToForm({}));
      if (fallback.id !== undefined && fallback.id !== null && fallback.id !== '') {
        setAccount({
          id: fallback.id,
          username: fallback.username || '',
          email: fallback.email || '',
          phone_number: fallback.phone_number || '',
        });
        setForm({ ...emptyForm, ...fallback.form });
      }
    } finally {
      setFetching(false);
    }
  }, [mergeStoredIdentity]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useLayoutEffect(() => {
    if (errorFocusKey === '_general' && fieldErrors._general) {
      const el = saveFeedbackRef.current;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      try {
        el.focus({ preventScroll: true });
      } catch {
        /* ignore */
      }
      return;
    }
    if (errorFocusKey && errorFocusKey !== '_general' && fieldErrors[errorFocusKey]) {
      const fid = ACCOUNT_FIELD_IDS[errorFocusKey];
      if (!fid) return;
      const el = document.getElementById(fid);
      if (!el) return;
      scrollAccountFormGroupIntoViewAndFocus(el);
    }
  }, [errorFocusKey, fieldErrors]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    clearFieldError(name);
    if (name === 'guardian_mobile') {
      setForm((prev) => ({ ...prev, guardian_mobile: sanitizePhoneDigitsInput(value, 15) }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePinChange = (e) => {
    clearFieldError('pincode');
    setForm((prev) => ({
      ...prev,
      pincode: e.target.value.replace(/\D/g, '').slice(0, 6),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setErrorFocusKey(null);

    const headerCheck = validateAccountHeader({
      ...account,
      phone_number: stripPhoneDigits(account.phone_number),
    });
    if (!headerCheck.ok) {
      setFieldErrors({ [headerCheck.field]: headerCheck.message });
      setErrorFocusKey(headerCheck.field);
      return;
    }

    const fieldCheck = validateProfileFormFields(form);
    if (!fieldCheck.ok) {
      setFieldErrors({ [fieldCheck.field]: fieldCheck.message });
      setErrorFocusKey(fieldCheck.field);
      return;
    }

    const userIdNum = Number(account.id);
    if (!Number.isFinite(userIdNum)) {
      setFieldErrors({ _general: 'Invalid account id.' });
      setErrorFocusKey('_general');
      return;
    }

    const stored = readStoredAuth();
    const token = stored.token;
    if (!token) {
      clearStoredAuth();
      setFieldErrors({ _general: SIGN_IN_REQUIRED_MESSAGE });
      setErrorFocusKey('_general');
      return;
    }

    const body = buildCreateProfileBody(
      {
        id: userIdNum,
        username: account.username.trim(),
        email: account.email.trim(),
        phone_number: stripPhoneDigits(account.phone_number),
      },
      form,
    );

    setSaving(true);
    try {
      const { res, data } = await apiCreateProfile(token, body);
      if (!res.ok) {
        clearStoredAuthIfUnauthorized(res.status);
        const parsed = parseCreateProfileValidationErrors(data);
        if (Object.keys(parsed.fieldErrors).length > 0) {
          setFieldErrors(parsed.fieldErrors);
          setErrorFocusKey(parsed.firstFocusField);
        } else {
          const msg = profileSaveErrorMessage(res.status, data);
          setFieldErrors({ _general: msg });
          setErrorFocusKey('_general');
        }
        return;
      }

      const resFirst = getFirstValue(data, ['firstName', 'first_name']);
      const resLast = getFirstValue(data, ['lastName', 'last_name']);
      const displayName =
        [resFirst, resLast].filter((x) => x && String(x).trim()).join(' ') ||
        `${form.first_name.trim()} ${form.last_name.trim()}`.trim();

      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const updatedUser = {
        ...parsed.user,
        id: data.id ?? userIdNum,
        name: displayName,
        email: data.email || body.email,
        mobile: data.phone_number || body.phone_number || String(body.phone_number),
      };
      localStorage.setItem(
        AUTH_STORAGE_KEY,
        JSON.stringify({
          ...parsed,
          user: updatedUser,
        }),
      );
      notifyAuthUpdated();
      setFieldErrors({});
      setErrorFocusKey(null);
      const successMsg =
        typeof data.message === 'string' && data.message.trim()
          ? data.message.trim()
          : 'Profile saved successfully.';
      navigate('/account', {
        replace: true,
        state: { profileSavedMessage: successMsg },
      });
    } catch (err) {
      const msg = err && typeof err.message === 'string' ? err.message : '';
      setFieldErrors({ _general: msg || 'Unable to save.' });
      setErrorFocusKey('_general');
    } finally {
      setSaving(false);
    }
  };

  if (fetching) {
    return (
      <div className="account-page">
        <div className="account-page__title-block">
          <h1>My Profile</h1>
          <p className="account-page__muted">Loading your profile…</p>
        </div>
      </div>
    );
  }

  const profileAge = form.date_of_birth ? ageFromIsoDateInput(form.date_of_birth) : null;
  const guardianRequired = profileAge != null && profileAge < 18;
  const invalidClass = (key) =>
    fieldErrors[key] ? 'account-page__input--invalid' : undefined;

  return (
    <div className="account-page">
      <div className="account-page__title-block">
        <h1>My Profile</h1>
        <p className="account-page__muted">Edit your DigiRasa account details.</p>
      </div>

      {loadError ? (
        <AccountSessionError
          message={loadError}
          returnPath="/account/details"
          signInSuffix="to continue"
          onRetry={loadProfile}
          retryLabel="Try loading again"
        />
      ) : null}

      <form
        className="account-profile-view account-update-page__form"
        onSubmit={handleSubmit}
        noValidate
      >
        <h2 className="account-profile-view__section-title">Account</h2>
        <dl className="account-profile-view__list">
          <ProfileReadonlyRow label="Username" value={account.username} />
          <ProfileReadonlyRow label="Email" value={account.email} onChange={() => setIdentityChangeType('email')} />
          <ProfileReadonlyRow
            label="Mobile"
            value={formatPhoneForDisplay(account.phone_number)}
            onChange={() => setIdentityChangeType('phone')}
          />
        </dl>
        <p className="account-profile-view__note">
          Username is fixed. Use Change beside email or mobile to verify and update it.
        </p>

        <div className="account-profile-view__section-head">
          <h2 className="account-profile-view__section-title">Profile</h2>
          <Link to="/account" className="account-profile-view__cancel-link">
            Cancel
          </Link>
        </div>
        <div className="account-profile-view__list account-profile-view__fields">
          <CompactField
            id="acct-fn"
            label="First name"
            error={<InlineFieldError fieldKey="first_name" fieldErrors={fieldErrors} />}
          >
            <input
              id="acct-fn"
              name="first_name"
              type="text"
              value={form.first_name}
              onChange={handleFormChange}
              autoComplete="given-name"
              aria-invalid={!!fieldErrors.first_name}
              aria-describedby={fieldErrors.first_name ? 'acct-fn-err' : undefined}
              className={invalidClass('first_name')}
            />
          </CompactField>
          <CompactField
            id="acct-ln"
            label="Last name"
            error={<InlineFieldError fieldKey="last_name" fieldErrors={fieldErrors} />}
          >
            <input
              id="acct-ln"
              name="last_name"
              type="text"
              value={form.last_name}
              onChange={handleFormChange}
              autoComplete="family-name"
              aria-invalid={!!fieldErrors.last_name}
              aria-describedby={fieldErrors.last_name ? 'acct-ln-err' : undefined}
              className={invalidClass('last_name')}
            />
          </CompactField>
          <CompactField
            id="acct-gender"
            label="Gender"
            error={<InlineFieldError fieldKey="gender" fieldErrors={fieldErrors} />}
          >
            <select
              id="acct-gender"
              name="gender"
              value={form.gender}
              onChange={handleFormChange}
              aria-invalid={!!fieldErrors.gender}
              aria-describedby={fieldErrors.gender ? 'acct-gender-err' : undefined}
              className={invalidClass('gender')}
            >
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </CompactField>
          <CompactField
            id="acct-dob"
            label="Date of birth"
            hint={!fieldErrors.date_of_birth ? 'Under 18 requires guardian details below.' : null}
            hintId="acct-dob-hint"
            error={<InlineFieldError fieldKey="date_of_birth" fieldErrors={fieldErrors} />}
          >
            <input
              id="acct-dob"
              name="date_of_birth"
              type="date"
              value={form.date_of_birth}
              onChange={handleFormChange}
              aria-invalid={!!fieldErrors.date_of_birth}
              aria-describedby={
                fieldErrors.date_of_birth ? 'acct-dob-err' : 'acct-dob-hint'
              }
              className={invalidClass('date_of_birth')}
            />
          </CompactField>
          <CompactField
            id="acct-country"
            label="Country"
            error={<InlineFieldError fieldKey="country" fieldErrors={fieldErrors} />}
          >
            <input
              id="acct-country"
              name="country"
              type="text"
              value={form.country}
              onChange={handleFormChange}
              autoComplete="country-name"
              aria-invalid={!!fieldErrors.country}
              aria-describedby={fieldErrors.country ? 'acct-country-err' : undefined}
              className={invalidClass('country')}
            />
          </CompactField>
          <CompactField
            id="acct-state"
            label="State"
            error={<InlineFieldError fieldKey="state" fieldErrors={fieldErrors} />}
          >
            <select
              id="acct-state"
              name="state"
              value={form.state}
              onChange={handleFormChange}
              aria-invalid={!!fieldErrors.state}
              aria-describedby={fieldErrors.state ? 'acct-state-err' : undefined}
              className={invalidClass('state')}
            >
              <option value="">Select state</option>
              {INDIAN_STATES.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
          </CompactField>
          <CompactField
            id="acct-city"
            label="City"
            error={<InlineFieldError fieldKey="city" fieldErrors={fieldErrors} />}
          >
            <input
              id="acct-city"
              name="city"
              type="text"
              value={form.city}
              onChange={handleFormChange}
              autoComplete="address-level2"
              aria-invalid={!!fieldErrors.city}
              aria-describedby={fieldErrors.city ? 'acct-city-err' : undefined}
              className={invalidClass('city')}
            />
          </CompactField>
          <CompactField
            id="acct-pin"
            label="Pin code"
            error={<InlineFieldError fieldKey="pincode" fieldErrors={fieldErrors} />}
          >
            <input
              id="acct-pin"
              name="pincode"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={form.pincode}
              onChange={handlePinChange}
              aria-invalid={!!fieldErrors.pincode}
              aria-describedby={fieldErrors.pincode ? 'acct-pin-err' : undefined}
              className={invalidClass('pincode')}
            />
          </CompactField>
        </div>

        <h2 className="account-profile-view__section-title">
          {guardianRequired ? 'Guardian *' : 'Guardian'}
        </h2>
        <div className="account-profile-view__list account-profile-view__fields">
          <CompactField
            id="acct-gn"
            label={`Guardian name${guardianRequired ? ' *' : ''}`}
            error={<InlineFieldError fieldKey="guardian_name" fieldErrors={fieldErrors} />}
          >
            <input
              id="acct-gn"
              name="guardian_name"
              type="text"
              value={form.guardian_name}
              onChange={handleFormChange}
              required={guardianRequired}
              aria-required={guardianRequired}
              aria-invalid={!!fieldErrors.guardian_name}
              aria-describedby={fieldErrors.guardian_name ? 'acct-gn-err' : undefined}
              className={invalidClass('guardian_name')}
            />
          </CompactField>
          <CompactField
            id="acct-ge"
            label={`Guardian email${guardianRequired ? ' *' : ''}`}
            error={<InlineFieldError fieldKey="guardian_email" fieldErrors={fieldErrors} />}
          >
            <input
              id="acct-ge"
              name="guardian_email"
              type="email"
              value={form.guardian_email}
              onChange={handleFormChange}
              required={guardianRequired}
              aria-required={guardianRequired}
              aria-invalid={!!fieldErrors.guardian_email}
              aria-describedby={fieldErrors.guardian_email ? 'acct-ge-err' : undefined}
              className={invalidClass('guardian_email')}
            />
          </CompactField>
          <CompactField
            id="acct-gm"
            label={`Guardian mobile${guardianRequired ? ' *' : ''}`}
            error={<InlineFieldError fieldKey="guardian_mobile" fieldErrors={fieldErrors} />}
          >
            <input
              id="acct-gm"
              name="guardian_mobile"
              type="tel"
              inputMode="numeric"
              maxLength={15}
              placeholder="919999999999"
              value={form.guardian_mobile}
              onChange={handleFormChange}
              required={guardianRequired}
              aria-required={guardianRequired}
              aria-invalid={!!fieldErrors.guardian_mobile}
              aria-describedby={fieldErrors.guardian_mobile ? 'acct-gm-err' : undefined}
              className={invalidClass('guardian_mobile')}
            />
          </CompactField>
        </div>

        {fieldErrors._general ? (
          <div
            ref={saveFeedbackRef}
            className="account-page__save-feedback"
            tabIndex={-1}
            aria-live="polite"
          >
            <AccountSessionError
              message={fieldErrors._general}
              returnPath="/account/details"
              signInSuffix="to continue"
              variant="inline"
              className="account-page__save-feedback-msg"
            />
          </div>
        ) : null}

        <div className="account-update-page__actions">
          <Link to="/account" className="account-update-page__cancel-btn">
            Cancel
          </Link>
          <button type="submit" className="account-update-page__save-btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
      {identityChangeType ? (
        <AccountIdentityChangeModal
          changeType={identityChangeType}
          currentEmail={account.email}
          currentPhone={account.phone_number}
          onClose={() => setIdentityChangeType(null)}
          onCompleted={(data) => {
            setAccount((current) => ({
              ...current,
              email: data.email || current.email,
              phone_number: data.phone_number || current.phone_number,
            }));
          }}
        />
      ) : null}
    </div>
  );
};

export default AccountUpdateDetails;
